import React, { useCallback, useContext, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  useTheme,
  Stack,
  CircularProgress,
  Button,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { SportEvent } from '../types/SportEventTypes';
import { TimeAdjuster, BoundaryExceededInfo } from './TimeAdjuster';
import { CameraButton } from './CameraButton';
import { ClipAspectRatioToggle } from './ClipAspectRatioToggle';

// Configurable time boundaries (in milliseconds)
// These control how far before/after the event the IN/OUT times can be set
// Rule: Minimum clip length = IN_MIN_OFFSET + OUT_MIN_OFFSET (must be at least 10 seconds total)
const TIME_BOUNDARY_CONFIG = {
  /** Minimum offset before event for IN time (in ms). Ensures clip starts at least this far before event */
  IN_MIN_OFFSET_MS: 5 * 1000, // 5 seconds before event (minimum)
  /** Maximum offset before event for IN time (in ms) */
  IN_MAX_OFFSET_MS: 2.5 * 60 * 1000, // 2 minutes before event (maximum)
  /** Minimum offset after event for OUT time (in ms). Ensures clip ends at least this far after event */
  OUT_MIN_OFFSET_MS: 5 * 1000, // 5 seconds after event (minimum)
  /** Maximum offset after event for OUT time (in ms) */
  OUT_MAX_OFFSET_MS: 2.5 * 60 * 1000, // 2 minutes after event (maximum)
  // Total minimum clip length: IN_MIN (5s) + OUT_MIN (5s) = 10 seconds

  // Display values for user-facing messages (more conservative than actual limits)
  IN_MAX_DISPLAY_MS: 2 * 60 * 1000, // Display "2 minutes" in messages
  OUT_MAX_DISPLAY_MS: 2 * 60 * 1000, // Display "2 minutes" in messages
};

import { formatDurationWithSeconds } from '@/shared/services/getDuration';
import { TimemachineActionsContext } from '@/features/timeMachine/components/TimeMachineStatusProvider';
import TrackedObjectSelectorDialog from '@/features/teams/components/TrackedObjectSelectorDialog';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { FixedCameraControls } from './FixedCameraControls';
import { ClipFeedbackMessage } from './ClipFeedbackMessage';
import { clipKey, markExportRequested } from '../hooks/clipDownloadFeedback';
import { useClipDownloadFeedback } from '../hooks/useClipDownloadFeedback';
import { useMediaContext } from '../context/MediaContext';
import { useFOVOverlay } from '../context/FOVOverlayContext';
import useServerState from '@/shared/hooks/useServerState';
import { useTimestampState } from '@/shared/hooks/websocket/useTimestamp';
import {
  AUTO_DIRECTOR_CAMERA,
  AUTO_DIRECTOR_CAMERA_LABEL,
  AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP,
  ClipCameraSelection,
  ClipModel,
  ClipStatus,
} from '@/features/clips/types';
import useEnqueueClipForExportMutation from '@/features/clips/hooks/useEnqueueClipForExportMutation';
import { useTranscodingProfiles } from '@/shared/context/TranscodingProfileContext';
import useCancelClipExportMutation from '@/features/clips/hooks/useCancelClipExportMutation';

import useConfirmationDialogContext from '@/shared/hooks/useConfirmationDialogContext';
import Tracking from '@/shared/services/tracking';
import type { AutoIsoId } from '@/features/autoIso/types/AutoIsoId';

interface Camera {
  id: number;
  displayName: string;
  isLive?: boolean;
}

interface ClipGroup {
  id: string | number;
  trackedObjectId?: string | null;
  inTime?: Date | string;
  outTime?: Date | string;
  isPersisted?: boolean;
  isCreating?: boolean;
  clips?: Array<{
    id?: number | string;
    cameraId: number | null;
    autoDirector?: boolean;
    status?: string;
  }>;
  persistedId?: string | number;
  outputProfileId?: number | null; // Transcoding profile assignment ID
}

interface ClipGroupItemProps {
  group: ClipGroup;
  index: number;
  event: SportEvent;
  availableCameras: Camera[];
  featuredPlayers: AutoIsoId[];
  onTimeChange: (groupId: string | number, field: 'inTime' | 'outTime', time: Date) => void;
  onCameraClick: (groupId: string | number, camera: ClipCameraSelection) => void;
  onDeleteClipGroup: (groupId: string | number) => void;
  onTrackedObjectChange: (groupId: string, entityId: string | null) => Promise<void>;
  onOutputModeChange?: (
    groupId: string | number,
    outputMode: 'portrait' | 'landscape',
  ) => Promise<void>;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  activeGroupId: string | null;
  setActiveGroupId: (id: string | null) => void;
}

export const ClipGroupItem: React.FC<ClipGroupItemProps> = ({
  group,
  index,
  event,
  availableCameras,
  featuredPlayers,
  onTimeChange,
  onCameraClick,
  onDeleteClipGroup,
  onTrackedObjectChange,
  onOutputModeChange,
  anchorEl,
  setAnchorEl,
  activeGroupId,
  setActiveGroupId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const timemachineActions = useContext(TimemachineActionsContext);
  const setConfirmationDialog = useConfirmationDialogContext();
  const { findPlayerById, objectOfPlay, game } = useCurrentGame();
  // Auto-director follows the tracked object, so a game with tracking off has
  // nothing for it to follow.
  const trackingDisabled = game.enableTracking === false;
  const { saveFixedClip, triggerFixedClipExport, isSavingFixedClip } = useMediaContext();
  const { selectedCamera, trackEntity } = useServerState();
  const [, setTimestamp] = useTimestampState();
  const [isChangingOutputMode, setIsChangingOutputMode] = React.useState(false);
  const enqueueClipForExportMutation = useEnqueueClipForExportMutation();
  const cancelClipExportMutation = useCancelClipExportMutation();

  const isUpdating = useMemo(
    () =>
      isChangingOutputMode ||
      isSavingFixedClip ||
      enqueueClipForExportMutation.isPending ||
      cancelClipExportMutation.isPending,
    [
      isChangingOutputMode,
      isSavingFixedClip,
      enqueueClipForExportMutation.isPending,
      cancelClipExportMutation.isPending,
    ],
  );

  // Format milliseconds to a human-readable duration
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes} minutes`;
  };

  // Handler for when user enters a time outside the allowed boundaries
  const handleBoundaryExceeded = useCallback(
    (field: 'IN' | 'OUT', _info: BoundaryExceededInfo) => {
      const minOffsetMs =
        field === 'IN'
          ? TIME_BOUNDARY_CONFIG.IN_MIN_OFFSET_MS
          : TIME_BOUNDARY_CONFIG.OUT_MIN_OFFSET_MS;
      const maxDisplayMs =
        field === 'IN'
          ? TIME_BOUNDARY_CONFIG.IN_MAX_DISPLAY_MS
          : TIME_BOUNDARY_CONFIG.OUT_MAX_DISPLAY_MS;
      const direction = field === 'IN' ? 'before' : 'after';

      const message = `${field} time adjusted: Must be between ${formatDuration(minOffsetMs)} and ${formatDuration(maxDisplayMs)} ${direction} the event.`;

      enqueueSnackbar(message, { variant: 'warning' });
    },
    [enqueueSnackbar],
  );

  const isDraft = 'isPersisted' in group && !group.isPersisted;
  const isCreating = 'isCreating' in group && group.isCreating;
  const groupId = 'persistedId' in group && group.persistedId ? group.persistedId : group.id;
  const clips = isDraft ? [] : (group as any).clips || [];
  const hasClips = clips.some((c: any) => {
    const status = c.status?.toUpperCase();
    return status && status !== 'NEW';
  });
  // With tracking off a fixed-camera clip is the only kind the group can
  // produce, so it opens pinned there rather than asking for a target it
  // cannot use. Display-level only — the group row keeps its own value, so
  // re-enabling tracking restores it.
  const trackedObjectId = trackingDisabled ? 'FIXED_CAMERA' : group.trackedObjectId || '';

  // Download-button states and the status line under them (Figma "Clip
  // download states"). The green check and its message stay for 5 s, a
  // failure's red X for 3 s. Downloading is always an explicit click on the
  // green arrow — a clip turning READY never starts a download by itself.
  const {
    getButtonState: getDownloadButtonState,
    message: feedbackMessage,
    dismissMessage: dismissFeedbackMessage,
  } = useClipDownloadFeedback<ClipModel>({
    groupId,
    clips,
  });
  /** Full camera name for a slot key, as used in the status line ("Left Rocker"). */
  const slotLabel = (key: string) =>
    key === AUTO_DIRECTOR_CAMERA
      ? AUTO_DIRECTOR_CAMERA_LABEL
      : (availableCameras.find((c) => String(c.id) === key)?.displayName ?? 'Camera');

  // Clicking a camera before picking a tracked object opens this selector.
  const trackedObjectButtonRef = useRef<HTMLButtonElement | null>(null);
  const openTrackedObjectSelector = () => {
    setActiveGroupId(String(groupId));
    setAnchorEl(trackedObjectButtonRef.current);
  };

  // Determine current output mode based on outputProfileId (for persisted) or outputMode (for drafts).
  // `resolvedOutputMode` is null when the mode is UNKNOWN: the map hasn't
  // loaded, or the group's outputProfileId isn't in the loaded map (the
  // assignment ids changed server-side after this tab loaded). Faking
  // 'landscape' in that state made the toggle unusable: clicking landscape
  // short-circuited as a no-op and clicking portrait re-sent portrait forever.
  const { getMode, profiles, refetch: refetchProfiles } = useTranscodingProfiles();
  const outputProfileId: number | null = (group as any).outputProfileId ?? null;
  const resolvedOutputMode: 'portrait' | 'landscape' | null = isDraft
    ? (group as any).outputMode || 'landscape'
    : profiles
      ? outputProfileId != null
        ? getMode(outputProfileId)
        : 'landscape' // legacy group without a profile — backend treats as landscape
      : null;
  const currentOutputMode: 'portrait' | 'landscape' = resolvedOutputMode ?? 'landscape';

  // Self-heal an unusable map: either it never loaded (the one-shot fetch at
  // page load failed) or it can't resolve this group's outputProfileId (the
  // assignment ids were reseeded/reassigned since this tab fetched them).
  // Refetch once per unresolved id — the guard prevents loops when the fetch
  // keeps failing or the id is genuinely dangling (deleted assignment).
  const staleMapRefetchedForId = useRef<number | null>(null);
  useEffect(() => {
    if (isDraft || outputProfileId == null) return;
    if (profiles && getMode(outputProfileId) !== null) return;
    if (staleMapRefetchedForId.current === outputProfileId) return;
    staleMapRefetchedForId.current = outputProfileId;
    refetchProfiles();
  }, [isDraft, profiles, outputProfileId, getMode, refetchProfiles]);

  // Manage FOV overlay state: ONLY active group updates context to avoid conflicts
  const { setActivePortraitGroupId } = useFOVOverlay();
  useEffect(() => {
    const isThisGroupActive = String(activeGroupId) === String(groupId);

    // Only the active group should update the context
    if (isThisGroupActive) {
      const shouldShowOverlay = currentOutputMode === 'portrait';
      setActivePortraitGroupId(shouldShowOverlay ? String(groupId) : null, {
        fixedCamera: trackedObjectId === 'FIXED_CAMERA',
      });
    }
  }, [
    activeGroupId,
    groupId,
    currentOutputMode,
    trackedObjectId,
    setActivePortraitGroupId,
  ]);

  // Confirmation dialog for destructive changes
  const confirmReset = useCallback(
    (onConfirm: () => void | Promise<void>) => {
      if (!setConfirmationDialog) {
        onConfirm();
        return;
      }

      setConfirmationDialog({
        title: 'Confirm Change',
        message: 'Downloads will need to be rendered again with this change.',
        confirmText: 'Okay',
        cancelText: 'Cancel',
        confirmButtonStyle: 'primary',
        onConfirm: async () => {
          await onConfirm();
          enqueueSnackbar(
            'Clips reset — click a camera to re-render with new timing.',
            // @ts-expect-error - custom variant
            { variant: 'clipReset' },
          );
        },
      });
    },
    [setConfirmationDialog, enqueueSnackbar],
  );

  // Handler for output mode (portrait/landscape) changes
  const handleOutputModeChange = useCallback(
    async (mode: 'portrait' | 'landscape') => {
      // Only skip when the current mode is KNOWN to already be the target.
      // When it's unknown (resolvedOutputMode === null) the click must go
      // through — otherwise a stale map freezes the toggle in both directions.
      if (resolvedOutputMode === mode || !onOutputModeChange) {
        return;
      }

      const performChange = async () => {
        setActiveGroupId(String(groupId));
        setActivePortraitGroupId(mode === 'portrait' ? String(groupId) : null, {
          fixedCamera: trackedObjectId === 'FIXED_CAMERA',
        });
        setIsChangingOutputMode(true);
        try {
          await onOutputModeChange(groupId, mode);
          const message = isDraft
            ? `Output mode set to ${mode}. This will be applied when you create clips.`
            : `Output mode changed to ${mode}. Existing clips will need to be re-downloaded.`;
          enqueueSnackbar(message, { variant: 'success' });
        } catch (error) {
          setActivePortraitGroupId(
            currentOutputMode === 'portrait' ? String(groupId) : null,
            { fixedCamera: trackedObjectId === 'FIXED_CAMERA' },
          );
          enqueueSnackbar(
            `Failed to change output mode: ${error instanceof Error ? error.message : 'Unknown error'}`,
            { variant: 'error' },
          );
        } finally {
          setIsChangingOutputMode(false);
        }
      };

      // For persisted groups with clips, show confirmation before changing mode
      if (!isDraft && hasClips) {
        confirmReset(performChange);
      } else {
        await performChange();
      }
    },
    [
      groupId,
      resolvedOutputMode,
      currentOutputMode,
      trackedObjectId,
      onOutputModeChange,
      isDraft,
      hasClips,
      enqueueSnackbar,
      confirmReset,
    ],
  );

  // Get clipGroup-specific times (convert string to Date if needed)
  const groupInTime = group.inTime
    ? group.inTime instanceof Date
      ? group.inTime
      : new Date(group.inTime)
    : new Date(event.timestamp || Date.now());
  const groupOutTime = group.outTime
    ? group.outTime instanceof Date
      ? group.outTime
      : new Date(group.outTime)
    : new Date(event.timestamp || Date.now());

  // Handlers for this specific clipGroup
  const handleInTimeChange = (seconds: number) => {
    if (!event.timestamp) return;
    const eventTime = new Date(event.timestamp);
    const newInTime = new Date(groupInTime.getTime() + seconds * 1000);

    // Constraints: IN can be from IN_MIN_OFFSET to IN_MAX_OFFSET before event
    const minInTime = eventTime.getTime() - TIME_BOUNDARY_CONFIG.IN_MAX_OFFSET_MS;
    const maxInTime = eventTime.getTime() - TIME_BOUNDARY_CONFIG.IN_MIN_OFFSET_MS;

    if (newInTime.getTime() >= minInTime && newInTime.getTime() <= maxInTime) {
      if (isDraft) {
        onTimeChange(groupId, 'inTime', newInTime);
        return;
      }

      if (hasClips) {
        confirmReset(() => onTimeChange(groupId, 'inTime', newInTime));
        return;
      }

      onTimeChange(groupId, 'inTime', newInTime);
    }
  };

  const handleOutTimeChange = (seconds: number) => {
    if (!event.timestamp) return;
    const eventTime = new Date(event.timestamp);
    const newOutTime = new Date(groupOutTime.getTime() + seconds * 1000);

    // Constraints: OUT can be from OUT_MIN_OFFSET to OUT_MAX_OFFSET after event
    const minOutTime = eventTime.getTime() + TIME_BOUNDARY_CONFIG.OUT_MIN_OFFSET_MS;
    const maxOutTime = eventTime.getTime() + TIME_BOUNDARY_CONFIG.OUT_MAX_OFFSET_MS;

    if (newOutTime.getTime() >= minOutTime && newOutTime.getTime() <= maxOutTime) {
      if (isDraft) {
        onTimeChange(groupId, 'outTime', newOutTime);
        return;
      }

      if (hasClips) {
        confirmReset(() => onTimeChange(groupId, 'outTime', newOutTime));
        return;
      }

      onTimeChange(groupId, 'outTime', newOutTime);
    }
  };

  const selectedPlayer = useMemo(() => {
    if (trackedObjectId === 'FIXED_CAMERA') return null;

    return findPlayerById(parseInt(trackedObjectId || '0'));
  }, [trackedObjectId, findPlayerById]);

  const isFixedCamera = trackedObjectId === 'FIXED_CAMERA';
  const isOrphanedTrackedObject =
    !!trackedObjectId && trackedObjectId !== '1' && !isFixedCamera && !selectedPlayer;
  const needsTrackedObject = !trackedObjectId || isOrphanedTrackedObject;

  // First enabled camera (used as fallback when capturing position)
  const firstEnabledCamera = availableCameras.find((c) => c.isLive);

  // Get the fixed camera clip (if exists)
  const fixedClip = isFixedCamera ? clips[0] : null;

  const handleTrackedObjectChange = useCallback(
    (nextTrackedObjectId: string | null) => {
      if (isDraft) {
        onTrackedObjectChange(groupId as string, nextTrackedObjectId);
        return;
      }

      if (hasClips) {
        confirmReset(() => onTrackedObjectChange(groupId as string, nextTrackedObjectId));
        return;
      }

      onTrackedObjectChange(groupId as string, nextTrackedObjectId);
    },
    [confirmReset, groupId, hasClips, isDraft, onTrackedObjectChange],
  );

  // Picking the fixed-camera sentinel is what persists a draft group, and
  // Set Position can only write to a persisted one. With the selector
  // disabled nothing is left to make that choice, so the group makes it
  // itself. Only fills an empty value — a group that already names a player
  // keeps it, so re-enabling tracking gets it back.
  useEffect(() => {
    if (!trackingDisabled || group.trackedObjectId) return;
    onTrackedObjectChange(groupId as string, 'FIXED_CAMERA');
  }, [trackingDisabled, group.trackedObjectId, groupId, onTrackedObjectChange]);

  const handleSetPosition = useCallback(
    async (ptz: { pan: number; tilt: number; zoom: number }, framingPresetId?: number) => {
      const cameraIdToUse =
        selectedCamera?.id || firstEnabledCamera?.id || availableCameras[0]?.id || 1;

      // Save position to backend (creates or updates clip with NEW status, no export)
      return saveFixedClip(groupId as number, {
        cameraId: cameraIdToUse,
        pan: ptz.pan,
        tilt: ptz.tilt,
        zoom: ptz.zoom,
        framingPresetId,
      });
    },
    [availableCameras, firstEnabledCamera, groupId, selectedCamera, saveFixedClip],
  );

  const liveCameraCount = availableCameras.filter((c) => c.isLive).length;
  const autoDirectorClip = clips.find((c: any) => c.autoDirector);

  // Figma's "Clip Type" field (Game View, node 5660:48443): Single keeps the
  // per-camera download row, Auto-Director collapses it to one Download Clip
  // button. Local UI state only; the persisted fact is the clip itself, so a
  // group that already holds an auto-director clip opens on that type.
  const [clipType, setClipType] = React.useState<'single' | 'auto'>(
    autoDirectorClip ? 'auto' : 'single',
  );
  const [clipTypeAnchorEl, setClipTypeAnchorEl] = React.useState<HTMLElement | null>(null);
  useEffect(() => {
    if (autoDirectorClip) setClipType('auto');
  }, [autoDirectorClip]);

  // Tracked here rather than off `clipType`, because the effect above also
  // writes it: a group that already holds an auto-director clip flips to 'auto'
  // on mount, which is not a choice anyone made. Re-picking the current type is
  // dropped for the same reason.
  const handleClipTypeChange = useCallback(
    (nextClipType: 'single' | 'auto') => {
      setClipTypeAnchorEl(null);
      if (nextClipType === clipType) return;
      setClipType(nextClipType);
      Tracking.getInstance().track('Change Clip Type', {
        category: 'Clips',
        clipType: nextClipType,
      });
    },
    [clipType],
  );
  const isAutoDirectorType = clipType === 'auto';

  // One export button per camera plus the Auto-Director entry. They share the
  // create / retry / cancel / download handling; only the clip they look up
  // and the selection handed to onCameraClick differ.
  const renderClipButton = ({
    key,
    label,
    selection,
    cameraIsAvailable,
    clip,
  }: {
    key: string;
    label: string;
    selection: ClipCameraSelection;
    cameraIsAvailable: boolean;
    clip: any;
  }) => {
    const clipStatusNormalized = clip?.status?.toUpperCase();

    // Determine tooltip based on clip status
    let tooltipTitle = '';
    if (clipStatusNormalized === ClipStatus.ERROR) {
      tooltipTitle = 'Click to retry export';
    } else if (clipStatusNormalized === ClipStatus.PROCESSING) {
      tooltipTitle = 'Click to cancel export';
    } else if (!clip && needsTrackedObject) {
      tooltipTitle = 'Select an object to track first';
    }

    const cameraButtonComponent = (
      <CameraButton
        camera={label}
        // Only a camera that is not live is disabled. Without a tracked
        // object the button stays at full strength and the click below
        // points the user at the selector instead of looking greyed out.
        disabled={!cameraIsAvailable}
        downloadState={getDownloadButtonState(key)}
        onClick={async () => {
          // Handle ERROR status: retry the export
          if (clipStatusNormalized === ClipStatus.ERROR && clip?.id) {
            try {
              markExportRequested(groupId, key);
              await enqueueClipForExportMutation.mutateAsync(clip.id);
              enqueueSnackbar('Retrying clip export...', { variant: 'info' });
            } catch (error) {
              enqueueSnackbar(
                `Failed to retry export: ${error instanceof Error ? error.message : 'Unknown error'}`,
                { variant: 'error' },
              );
            }
            return;
          }

          // Handle PROCESSING status: cancel the export
          // A cancel that is still in flight is neither retriable nor cancellable.
          if (clipStatusNormalized === ClipStatus.CANCELING) {
            return;
          }

          if (clipStatusNormalized === ClipStatus.PROCESSING && clip?.id) {
            try {
              await cancelClipExportMutation.mutateAsync({ clipId: clip.id });
              enqueueSnackbar('Canceling clip export...', { variant: 'info' });
            } catch (error) {
              enqueueSnackbar(
                `Failed to cancel export: ${error instanceof Error ? error.message : 'Unknown error'}`,
                { variant: 'error' },
              );
            }
            return;
          }

          // Default behavior: create new clip. Without a tracked object the
          // click opens the selector instead.
          if (needsTrackedObject) {
            openTrackedObjectSelector();
            return;
          }
          onCameraClick(groupId, selection);
        }}
        onDownloadClick={() => {
          if (clip && timemachineActions?.downloadClip) {
            timemachineActions.downloadClip(clip);
          }
        }}
        sx={{
          color: 'inherit',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
        }}
      />
    );

    return tooltipTitle ? (
      <Tooltip key={key} title={tooltipTitle} arrow placement="top">
        <span>{cameraButtonComponent}</span>
      </Tooltip>
    ) : (
      <React.Fragment key={key}>{cameraButtonComponent}</React.Fragment>
    );
  };

  const handlePlayClick = useCallback(() => {
    setTimestamp?.(groupInTime);

    // Auto-switch the player to follow this clip's tracked entity. Skip
    // fixed-camera clips (no entity) and unset selections. Mirrors the
    // seek+track pattern used by handleSeekToShift in PlayByPlayPanel.
    if (trackedObjectId && trackedObjectId !== 'FIXED_CAMERA') {
      const numericId = Number(trackedObjectId);
      if (Number.isFinite(numericId)) {
        try {
          trackEntity({ id: numericId }, 'ClipGroupPlay');
        } catch (err) {
          console.error('Failed to track entity from clip play:', err);
        }
      }
    }
  }, [groupInTime, setTimestamp, trackedObjectId, trackEntity]);

  const handleDeleteClick = useCallback(() => {
    if (!setConfirmationDialog) {
      onDeleteClipGroup(groupId);
      return;
    }

    setConfirmationDialog({
      title: 'Delete Clip',
      message: 'Are you sure you want to delete this clip ?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonStyle: 'error',
      onConfirm: async () => {
        await onDeleteClipGroup(groupId);
      },
    });
  }, [groupId, onDeleteClipGroup, setConfirmationDialog]);

  return (
    <Stack
      direction="column"
      spacing={1}
      onClick={() => {
        setActiveGroupId(String(groupId));
      }}
      sx={{
        // Figma clip-item (Game View, node 4348:16338): wash-900 card, 8px
        // radius, 6px top / 12px side and bottom padding, 8px between rows.
        border: `1px solid ${theme.palette.cx.border.default}`,
        backgroundColor: theme.palette.cx.bg.surface2,
        borderRadius: '8px',
        pt: '6px',
        px: 1.5,
        pb: 1.5,
        m: 1.5,
        mt: index === 0 ? 1.5 : 0,
        position: 'relative',
        opacity: isCreating || isUpdating ? 0.6 : 1,
        pointerEvents: isCreating || isUpdating ? 'none' : 'auto',
        cursor: 'pointer',
      }}
    >
      {(isCreating || isUpdating) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={24} sx={{ color: 'white' }} />
        </Box>
      )}

      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            color: theme.palette.cx.washDark[400],
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
          }}
        >
          Clip Duration: {formatDurationWithSeconds(groupInTime, groupOutTime)}
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
          <IconButton
            onClick={handlePlayClick}
            disabled={isCreating}
            aria-label="Play from clip start"
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              border: '1px solid white',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 10V19M9 17.9809V11.02C9 10.2794 9.7775 9.81486 10.4025 10.1826L16.3258 13.6634C16.4694 13.7477 16.5888 13.8696 16.6718 14.0167C16.7548 14.1638 16.7985 14.3308 16.7985 14.5009C16.7985 14.6709 16.7548 14.838 16.6718 14.985C16.5888 15.1321 16.4694 15.254 16.3258 15.3383L10.4025 18.8191C10.2599 18.903 10.0983 18.9467 9.93402 18.9461C9.76974 18.9455 9.60851 18.9005 9.46647 18.8156C9.32443 18.7307 9.20657 18.6089 9.12471 18.4624C9.04284 18.3159 8.99983 18.1498 9 17.9809Z"
                stroke="white"
                strokeWidth="0.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
          <IconButton
            onClick={handleDeleteClick}
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              color: '#FFF',
              border: '1px solid #F04438',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#F04438',
                backgroundColor: 'rgba(240, 68, 56, 0.1)',
              },
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75006 3.28117C8.41017 3.28117 8.07157 3.2866 7.73433 3.29739C7.21123 3.31413 6.79457 3.75268 6.79457 4.29514V4.47066C7.4411 4.42914 8.09314 4.40806 8.75006 4.40806C9.40698 4.40806 10.059 4.42914 10.7056 4.47066V4.29514C10.7056 3.75268 10.2889 3.31413 9.76578 3.29739C9.42855 3.2866 9.08994 3.28117 8.75006 3.28117ZM11.4347 4.52633V4.29514C11.4347 3.37827 10.7246 2.59852 9.7891 2.5686C9.44409 2.55756 9.09772 2.552 8.75006 2.552C8.4024 2.552 8.05603 2.55756 7.71102 2.5686C6.77556 2.59852 6.0654 3.37827 6.0654 4.29514V4.52633C5.44603 4.58115 4.83202 4.65476 4.22397 4.74659C4.00979 4.77894 3.79634 4.81354 3.58367 4.85039C3.38527 4.88475 3.25229 5.07345 3.28666 5.27185C3.32103 5.47025 3.50972 5.60322 3.70812 5.56885C3.78703 5.55518 3.86606 5.54183 3.94519 5.52879L4.57556 13.7237C4.64596 14.6389 5.40911 15.3456 6.32702 15.3456H11.1731C12.091 15.3456 12.8542 14.6389 12.9246 13.7237L13.5549 5.52879C13.6341 5.54183 13.7131 5.55518 13.792 5.56885C13.9904 5.60322 14.1791 5.47025 14.2135 5.27185C14.2478 5.07345 14.1149 4.88475 13.9165 4.85039C13.7038 4.81354 13.4903 4.77894 13.2762 4.74659C12.6681 4.65476 12.0541 4.58115 11.4347 4.52633ZM12.8321 5.41894C12.2409 5.33664 11.644 5.27195 11.042 5.22544C10.2858 5.167 9.52144 5.13723 8.75006 5.13723C7.97868 5.13723 7.21433 5.167 6.45807 5.22544C5.8561 5.27195 5.25924 5.33664 4.66806 5.41894L5.30258 13.6677C5.34376 14.203 5.79013 14.6164 6.32702 14.6164H11.1731C11.71 14.6164 12.1564 14.203 12.1975 13.6677L12.8321 5.41894ZM7.04061 6.72841C7.24181 6.72067 7.41119 6.8775 7.41893 7.07871L7.63309 12.6469C7.64083 12.8481 7.484 13.0175 7.28279 13.0252C7.08159 13.033 6.91221 12.8761 6.90447 12.6749L6.69031 7.10673C6.68257 6.90553 6.8394 6.73615 7.04061 6.72841ZM10.4595 6.72841C10.6607 6.73615 10.8176 6.90553 10.8098 7.10673L10.5957 12.6749C10.5879 12.8761 10.4185 13.033 10.2173 13.0252C10.0161 13.0175 9.85929 12.8481 9.86702 12.6469L10.0812 7.07871C10.0889 6.8775 10.2583 6.72067 10.4595 6.72841Z"
                fill="white"
              />
            </svg>
          </IconButton>
        </Stack>
      </Stack>

      {/* Tracked Object Selector Popover */}
      <TrackedObjectSelectorDialog
        open={activeGroupId === String(groupId) && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        value={trackedObjectId}
        setFixedCameraClip={() => {
          handleTrackedObjectChange('FIXED_CAMERA');
        }}
        selectTrackedObject={(ent: any) => {
          if (ent?.id) {
            handleTrackedObjectChange(String(ent.id));
          }
        }}
        //@ts-expect-error
        featuredPlayers={featuredPlayers}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: event.timestamp
            ? 'minmax(0, 1fr) minmax(112px, 0.58fr) minmax(0, 1fr)'
            : 'repeat(2, minmax(0, 1fr))',
          columnGap: 3,
          alignItems: 'center',
        }}
      >
        <TimeAdjuster
          title="IN"
          time={groupInTime}
          onTimeChange={handleInTimeChange}
          minTime={
            event.timestamp
              ? new Date(
                  new Date(event.timestamp).getTime() - TIME_BOUNDARY_CONFIG.IN_MAX_OFFSET_MS,
                )
              : undefined
          }
          maxTime={
            event.timestamp
              ? new Date(
                  new Date(event.timestamp).getTime() - TIME_BOUNDARY_CONFIG.IN_MIN_OFFSET_MS,
                )
              : undefined
          }
          onBoundaryExceeded={(info) => handleBoundaryExceeded('IN', info)}
          sx={{ minWidth: 0 }}
        />
        {event.timestamp && <TimeAdjuster title="EVENT" time={event.timestamp} readOnly />}
        <TimeAdjuster
          title="OUT"
          time={groupOutTime}
          onTimeChange={handleOutTimeChange}
          minTime={
            event.timestamp
              ? new Date(
                  new Date(event.timestamp).getTime() + TIME_BOUNDARY_CONFIG.OUT_MIN_OFFSET_MS,
                )
              : undefined
          }
          maxTime={
            event.timestamp
              ? new Date(
                  new Date(event.timestamp).getTime() + TIME_BOUNDARY_CONFIG.OUT_MAX_OFFSET_MS,
                )
              : undefined
          }
          onBoundaryExceeded={(info) => handleBoundaryExceeded('OUT', info)}
          sx={{ minWidth: 0 }}
        />
      </Box>

      <Box
        sx={{
          borderRadius: '6px',
          backgroundColor: theme.palette.cx.bg.surface,
          px: 1.5,
          py: '10px',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: theme.palette.cx.washDark[300],
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                mb: 0.5,
              }}
            >
              Tracked Object
            </Typography>

            <Tooltip
              title={trackingDisabled ? AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP : ''}
              arrow
              placement="top"
            >
              {/* A disabled button fires no mouse events, so the tooltip
                  needs a wrapper that is still interactive. */}
              <Box component="span" sx={{ display: 'block' }}>
                <Button
                  ref={trackedObjectButtonRef}
                  onClick={openTrackedObjectSelector}
                  disabled={isCreating || trackingDisabled}
                  aria-haspopup="dialog"
                  aria-expanded={activeGroupId === String(groupId) && Boolean(anchorEl)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    height: 28,
                    justifyContent: 'space-between',
                    // Unset reads as a placeholder over a brighter rule so the
                    // control is clearly the thing to click first.
                    color: trackedObjectId
                      ? theme.palette.cx.light[100]
                      : theme.palette.cx.text.secondary,
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    textTransform: 'none',
                    p: 0,
                    borderBottom: `1px solid ${
                      trackedObjectId ? theme.palette.cx.border.subtle : theme.palette.cx.text.muted
                    }`,
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: theme.palette.cx.light[100],
                      borderBottomColor: theme.palette.cx.text.secondary,
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.cx.text.muted,
                      borderBottomColor: theme.palette.cx.border.subtle,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      minWidth: 0,
                      flex: 1,
                      textAlign: 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {trackedObjectId ? (
                      trackedObjectId === '1' ? (
                        <Box component="span" sx={{ textTransform: 'capitalize' }}>
                          {objectOfPlay}
                        </Box>
                      ) : isFixedCamera ? (
                        'Fixed Camera Clip'
                      ) : isOrphanedTrackedObject ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            color: '#F79009',
                            fontSize: 13,
                          }}
                        >
                          <AlertTriangle size={14} />
                          Player not found
                        </Box>
                      ) : selectedPlayer ? (
                        `#${selectedPlayer.playerNumber} ${selectedPlayer.displayName}`
                      ) : null
                    ) : (
                      'Select tracked object'
                    )}
                  </Box>
                  <ChevronRight
                    size={16}
                    style={{ color: 'white', marginLeft: 4, flexShrink: 0 }}
                  />
                </Button>
              </Box>
            </Tooltip>
          </Box>

          {!isFixedCamera && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: theme.palette.cx.washDark[300],
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  mb: 0.5,
                }}
              >
                Clip Type
              </Typography>

              <Button
                onClick={(clickEvent) => setClipTypeAnchorEl(clickEvent.currentTarget)}
                disabled={isCreating}
                aria-haspopup="menu"
                aria-expanded={Boolean(clipTypeAnchorEl)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  height: 28,
                  justifyContent: 'space-between',
                  color: theme.palette.cx.light[100],
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  textTransform: 'none',
                  p: 0,
                  borderBottom: `1px solid ${theme.palette.cx.border.subtle}`,
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    borderBottomColor: theme.palette.cx.border.default,
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.cx.text.muted,
                    borderBottomColor: theme.palette.cx.border.subtle,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    minWidth: 0,
                    flex: 1,
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {clipType === 'auto' ? AUTO_DIRECTOR_CAMERA_LABEL : 'Single'}
                </Box>
                <ChevronRight size={16} style={{ color: 'white', marginLeft: 4, flexShrink: 0 }} />
              </Button>
              <Menu
                anchorEl={clipTypeAnchorEl}
                open={Boolean(clipTypeAnchorEl)}
                onClose={() => setClipTypeAnchorEl(null)}
              >
                <MenuItem
                  selected={clipType === 'single'}
                  onClick={() => handleClipTypeChange('single')}
                >
                  Single
                </MenuItem>
                <MenuItem
                  selected={clipType === 'auto'}
                  onClick={() => handleClipTypeChange('auto')}
                >
                  {AUTO_DIRECTOR_CAMERA_LABEL}
                </MenuItem>
              </Menu>
            </Box>
          )}

          <ClipAspectRatioToggle
            value={currentOutputMode}
            onChange={handleOutputModeChange}
            disabled={isCreating || isChangingOutputMode}
          />
        </Box>

        {isFixedCamera ? (
          <FixedCameraControls
            clip={fixedClip}
            availableCameras={availableCameras}
            currentSelectedCameraId={selectedCamera?.id}
            isSaving={isSavingFixedClip}
            downloadState={getDownloadButtonState(fixedClip ? clipKey(fixedClip) : '')}
            onSetPosition={handleSetPosition}
            onExport={async () => {
              if (!fixedClip?.id) {
                return;
              }
              markExportRequested(groupId, clipKey(fixedClip));
              // Check if there's a READY clip that would be replaced
              if (fixedClip.status === 'READY') {
                confirmReset(async () => {
                  await triggerFixedClipExport(fixedClip.id);
                });
              } else {
                await triggerFixedClipExport(fixedClip.id);
              }
            }}
            onDownloadClick={() => {
              if (fixedClip && timemachineActions?.downloadClip) {
                timemachineActions.downloadClip(fixedClip);
              }
            }}
          />
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              overflowX: 'auto',
            }}
          >
            {isAutoDirectorType
              ? renderClipButton({
                  key: 'auto-director',
                  label: 'Download Clip',
                  selection: AUTO_DIRECTOR_CAMERA,
                  // The player needs at least two feeds to cut between.
                  cameraIsAvailable: liveCameraCount >= 2,
                  clip: autoDirectorClip,
                })
              : availableCameras.map((camera) =>
                  renderClipButton({
                    key: String(camera.id),
                    label: camera.displayName,
                    selection: camera.id,
                    cameraIsAvailable: Boolean(camera.isLive),
                    clip: clips.find((c: any) => !c.autoDirector && c.cameraId === camera.id),
                  }),
                )}
          </Stack>
        )}
      </Box>

      {feedbackMessage && (
        <ClipFeedbackMessage
          kind={feedbackMessage.kind}
          cameraName={slotLabel(feedbackMessage.key)}
          onDismiss={dismissFeedbackMessage}
        />
      )}
    </Stack>
  );
};

export default ClipGroupItem;
